import { useSeo } from '../lib/seo';
/**
 * /accessible-days-out — editorial landing page for visitors with access needs.
 *
 * Pulls live data from the same `/attractions` filter we use on /explore, but
 * pre-curates three themed lists (step-free, Changing Places, sensory-friendly)
 * with editorial intros so this doesn't feel like a generic filtered grid.
 *
 * The disclaimer banner sits ABOVE the lists so visitors know what claims we
 * stand behind, and how to report inaccuracies.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Accessibility, ShieldCheck, Ear, ExternalLink } from 'lucide-react';
import api from '../lib/api';
import AttractionCard from '../components/AttractionCard';
import { AccessibilityDisclaimer } from '../components/Accessibility';

const SECTIONS = [
  // Step-free curated list suspended pending better data coverage.
  // {
  //   key: 'step_free',
  //   icon: Accessibility,
  //   title: 'Step-free days out',
  //   intro:
  //     "Days out across Leicestershire where the venues have confirmed step-free entry. We've cross-checked these against the venues themselves and OpenStreetMap's open data; we don't include anywhere that's merely \"likely\" step-free.",
  // },
  {
    key: 'changing_places',
    icon: ShieldCheck,
    title: 'Changing Places',
    intro:
      "Venues with a fully-equipped Changing Places toilet — the gold-standard adult-changing facility. There are a small but growing number of these in Leicestershire, drawn from the Changing Places registry and venue self-declaration.",
  },
  {
    key: 'sensory_friendly',
    icon: Ear,
    title: 'Sensory-friendly sessions',
    intro:
      "Venues offering quiet hours, autism-friendly times, relaxed performances or sensory bags. Dates and times vary — check the venue's website for the next one.",
  },
];

const PAGE_TITLE = 'Accessible days out in Leicestershire';
const PAGE_DESCRIPTION =
  "Curated days out across Leicestershire for visitors with access needs — step-free venues, places with Changing Places toilets, and sensory-friendly sessions. Independent, locally-researched.";

export default function AccessibleDaysOutPage() {
  useSeo(
    'Accessible Days Out in Leicestershire | Go Leicestershire',
    'Venues with step-free access, Changing Places toilets, hearing loops and more — plan an accessible day out in Leicestershire with confidence.'
  );
  const [data, setData] = useState({}); // { step_free: {items}, changing_places: {items}, sensory_friendly: {items} }
  useEffect(() => {
    SECTIONS.forEach((s) => {
      api.get(`/attractions?${s.key}=true&limit=12`).then((r) => {
        setData((prev) => ({ ...prev, [s.key]: r.data }));
      });
    });
  }, []);

  return (
    <div data-testid="accessible-days-page" className="bg-base">
      <Helmet>
        <title>{`${PAGE_TITLE} · GoLeicestershire`}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="robots" content="index,follow" />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
      </Helmet>

      {/* HERO */}
      <section className="bg-stone border-b border-charcoal/10">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-16 md:py-20">
          <div className="label text-heritage mb-3">For visitors with access needs</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-6">
            Accessible days out in <em className="not-italic text-heritage">Leicestershire</em>
          </h1>
          <p className="text-lg md:text-xl text-charcoal/75 max-w-2xl leading-relaxed">
            Curated lists from across the county, drawn from venue-confirmed information
            and open public data sources. Honest about what we know and what we don&apos;t —
            and easy to tell us when we&apos;ve got something wrong.
          </p>
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-6 lg:px-12 py-10 md:py-12">
        <AccessibilityDisclaimer variant="block" />
      </section>

      {/* SECTIONS */}
      {SECTIONS.map((s) => {
        const items = data[s.key]?.items || [];
        const Icon = s.icon;
        return (
          <section
            key={s.key}
            className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10 md:py-14 border-t border-charcoal/10"
            data-testid={`accessible-section-${s.key}`}
          >
            <div className="flex flex-wrap items-end justify-between gap-4 md:gap-6 mb-6 md:mb-8">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 mb-3">
                  <span className="w-9 h-9 rounded-full bg-forest/15 text-forest flex items-center justify-center">
                    <Icon size={16} />
                  </span>
                  <span className="label text-heritage">{s.title}</span>
                </div>
                <p className="text-charcoal/75 text-base md:text-lg leading-relaxed">{s.intro}</p>
              </div>
              <Link
                to={`/explore?${s.key}=true`}
                data-testid={`accessible-section-${s.key}-more`}
                className="inline-flex items-center gap-2 font-semibold hover:text-heritage text-sm"
              >
                See all in Explore <ArrowRight size={14} />
              </Link>
            </div>

            {data[s.key] === undefined ? (
              <div className="text-sm text-muted">Loading…</div>
            ) : items.length === 0 ? (
              <div className="bg-white border border-dashed border-charcoal/15 rounded-2xl p-6 text-sm text-charcoal/70">
                We don&apos;t yet have any venues confirmed in this category. We&apos;re adding
                more all the time as venues self-declare — and you can{' '}
                <a href="mailto:accessibility@goleicestershire.com" className="text-heritage hover:underline font-semibold">
                  let us know
                </a>{' '}
                about a venue we should include.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {items.slice(0, 6).map((a) => (
                  <AttractionCard key={a.id} a={a} />
                ))}
              </div>
            )}
          </section>
        );
      })}

      {/* Resources strip — link out rather than re-creating */}
      <section className="bg-stone border-t border-charcoal/10 mt-6">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-14 md:py-16">
          <div className="label text-heritage mb-3">Further reading</div>
          <h2 className="font-display text-2xl md:text-3xl leading-tight mb-6">More detailed UK access guides</h2>
          <p className="text-charcoal/75 leading-relaxed max-w-2xl mb-6">
            For the most detailed, photo-led access information — entrance widths, toilet
            dimensions, signed languages on tours — these two independent organisations are
            our recommended next read.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a
              href="https://www.accessable.co.uk/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white border border-charcoal/10 rounded-2xl p-5 hover:border-heritage/50 transition-colors block"
              data-testid="accessible-resource-accessable"
            >
              <div className="font-semibold mb-1">AccessAble</div>
              <div className="text-sm text-charcoal/70 mb-3">
                The UK&apos;s most detailed venue access guides. Free to use.
              </div>
              <div className="inline-flex items-center gap-1 text-sm font-semibold text-heritage">
                Visit AccessAble <ExternalLink size={12} />
              </div>
            </a>
            <a
              href="https://www.euansguide.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white border border-charcoal/10 rounded-2xl p-5 hover:border-heritage/50 transition-colors block"
              data-testid="accessible-resource-euans"
            >
              <div className="font-semibold mb-1">Euan&apos;s Guide</div>
              <div className="text-sm text-charcoal/70 mb-3">
                Disabled-access reviews written by disabled visitors and their families.
              </div>
              <div className="inline-flex items-center gap-1 text-sm font-semibold text-heritage">
                Visit Euan&apos;s Guide <ExternalLink size={12} />
              </div>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
